/**
 * How /creatives words what it shows (spec §3.3, §4.3, §4.4, §6): a creative's name, age, Result and cost per Result,
 * engagement, chips, box 1's choices, the empty line and the footnotes. The page and the CSV share every word here, so
 * the file says what the page says. Every word is a literal t('…') key, so the locale integrity test sees it; channel
 * names come from the one file of platform names (D2's CHANNEL_LABEL, platformShortLabel). null is "—", never 0. Pure.
 *
 * @module services/creatives/creativeText
 */
import { AD_CHANNEL_IDS, isFiltering } from '@/services/channelFilter';
import { CHANNEL_LABEL, fmtShortDate, type TFn } from '@/services/channels/channelFormat';
import { idPrefix } from '@/services/channels/channelNaming';
import {
  FATIGUE_CTR_DROP, MIN_CLICKS_30D, MIN_CONVERSIONS_30D, MIN_IMPRESSIONS_30D, MIN_POOL_SIZE,
} from '@/services/insights/insightConstants';
import { DASH, fmtCount, fmtMoney, fmtResult, objectiveText } from '@/services/overview/overviewFormat';
import type { ObjectiveFilter } from '@/services/overview/overviewTypes';
import type { ResultMetric } from '@/services/targets/resultMetric';
import { platformShortLabel } from '@/utils/platformLabel';
import { creativeFormatLabel } from './creativeFormat';
import type { CreativeAge, CreativeFilters, CreativeRow, CreativeSort, CreativeTableData, FormatFilter } from './creativeTypes';

/** The filters an empty table names (R12). */
export type AppliedFilters = Pick<CreativeFilters, 'channels' | 'objective' | 'format'>;

/** The ad name as stored; a blank one reads "(ไม่มีชื่อ) · <external ID>", or the id's first 8 characters without one. */
export const creativeNameOf = (r: Pick<CreativeRow, 'name' | 'externalAdId' | 'key'>, t: TFn): string =>
  (r.name.trim() !== '' ? r.name : t('creatives.unnamed', { id: r.externalAdId ?? idPrefix(r.key) }));

/** R10: "44 วัน"; "≥ 44 วัน" when the creative may be older than our data; "—" without a day with impressions. */
export const ageText = (age: CreativeAge | null, t: TFn): string => {
  if (age === null) return DASH;
  return age.atLeast ? t('creatives.age.atLeast', { n: age.days }) : t('creatives.age.days', { n: age.days });
};

/**
 * The metric a Result counts; literal keys. Reach reads "Reach รวมรายวัน" (DEC3 §1 Q4, rules-04 C2): D3's own key,
 * holding the Result definition's words.
 */
export const resultMetricText = (metric: ResultMetric, t: TFn): string => {
  if (metric === 'impressions') return t('creatives.metric.impressions');
  if (metric === 'reach') return t('creatives.metric.reach');
  if (metric === 'clicks') return t('creatives.metric.clicks');
  return t('creatives.metric.conversions');
};

/**
 * The Result cell (spec §3.3): D1's figure ("ไม่ได้รายงาน" when the creative spent and the platform sent no figure) and
 * the metric it counts; with no Result ("—"), no metric either.
 */
export const resultCellOf = (
  r: Pick<CreativeRow, 'metric' | 'result' | 'resultState'>, t: TFn,
): { value: string; metric: string | null } => ({
  value: fmtResult(r, t),
  metric: r.metric === null || r.resultState === 'none' ? null : resultMetricText(r.metric, t),
});

/** R19: an Awareness cost is per 1,000 impressions or reach, so it carries "/1,000" in the column it shares. */
export const costPerResultText = (r: Pick<CreativeRow, 'costPerResult' | 'objective'>, currency: string | null, t: TFn): string => {
  const money = fmtMoney(r.costPerResult, currency);
  return money !== DASH && r.objective === 'awareness' ? `${money}${t('creatives.perThousand')}` : money;
};

/**
 * An engagement count: D3's reactions + comments + shares, or one of them in D2's box 5. NULL (every part NULL) means
 * the platform sent none: D1's "ไม่ได้รายงาน" (rules-04 D4), never 0.
 */
export const engagementText = (n: number | null, t: TFn): string => (n === null ? t('overview.notReported') : fmtCount(n));

/** "Top · Facebook · Conversion" (spec §4.3). */
export const topChipText = (r: Pick<CreativeRow, 'platform' | 'objective'>, t: TFn): string =>
  t('creatives.chip.top', { channel: CHANNEL_LABEL[r.platform], objective: objectiveText(r.objective, t) });

/** "Fatigue ▼31% CTR": the drop rounded to a whole percent (R7). */
export const fatigueChipText = (drop: number, t: TFn): string => t('creatives.chip.fatigue', { pct: Math.round(drop * 100) });

/** A row's labels in the chips' order, Top · Fatigue · n<500: the CSV's "ป้าย" column. */
export const labelTextsOf = (
  r: Pick<CreativeRow, 'platform' | 'objective' | 'top' | 'fatigueDrop' | 'lowSample'>, t: TFn,
): string[] => [
  ...(r.top ? [topChipText(r, t)] : []),
  ...(r.fatigueDrop === null ? [] : [fatigueChipText(r.fatigueDrop, t)]),
  ...(r.lowSample ? [t('overview.lowSample')] : []),
];

/** Box 1's sort names; literal keys. */
export const sortLabel = (s: CreativeSort, t: TFn): string => {
  if (s === 'cpr') return t('creatives.sort.cpr');
  if (s === 'ctr') return t('creatives.sort.ctr');
  return t('creatives.sort.spend');
};

/** Box 1's format choices: "ทั้งหมด", then the shared format words. */
export const formatFilterLabel = (f: FormatFilter, t: TFn): string =>
  (f === 'all' ? t('creatives.format.all') : creativeFormatLabel(f, t));

/** The ticked channels' names in the menu's order; none when every channel shows (every one ticked = none, D1's isFiltering). */
const tickedChannels = (channels: readonly string[]): string[] => {
  const selected = new Set(channels);
  return isFiltering(selected) ? AD_CHANNEL_IDS.filter((c) => selected.has(c)).map(platformShortLabel) : [];
};

/** The channel filter as the print line and the CSV say it: "Facebook, TikTok", or "ทุก channel". */
export const channelFilterText = (channels: readonly string[], t: TFn): string => {
  const ticked = tickedChannels(channels);
  return ticked.length > 0 ? ticked.join(', ') : t('creatives.allChannels');
};

/** The objective filter as the print line and the CSV say it: "ทุก objective", or the objective. */
export const objectiveFilterText = (o: ObjectiveFilter, t: TFn): string =>
  (o === 'all' ? t('looker.objectives.all') : objectiveText(o, t));

/** The channel, objective and format filters that apply, in that order: "Facebook, TikTok · Conversion · Video". */
export const filterWordsOf = (f: AppliedFilters, t: TFn): string => {
  const objective = f.objective === 'all' ? '' : objectiveText(f.objective, t);
  const format = f.format === 'all' ? '' : creativeFormatLabel(f.format, t);
  return [tickedChannels(f.channels).join(', '), objective, format].filter((w) => w !== '').join(' · ');
};

/** R12: no creative in the page currency → "ไม่มี creative ในช่วงนี้ · <scope>"; else the filters emptied it, and are named. */
export const emptyTextOf = (data: Pick<CreativeTableData, 'inCurrency'>, scope: string, f: AppliedFilters, t: TFn): string =>
  (data.inCurrency === 0
    ? t('creatives.empty', { scope })
    : t('creatives.emptyFilters', { scope, filters: filterWordsOf(f, t) }));

export interface FootnoteInput {
  /** The Result window's last day; null = there is none (every day of the period is in the cut). */
  resultEnd: string | null;
  /** The Result definition could not be read (R6): Awareness reads "—". */
  mappingFailed: boolean;
  /** Awareness counts reach and an Awareness creative is in the table (rules-04 C2): D1's caveat, as box 3 shows it. */
  reach: boolean;
  /** Creatives dropped because their account's currency is unknown (R14). */
  unknownCurrency: number;
}

/**
 * The rules under the table, on the page and in the file: the labels, Performance Max, View rate. The labels' note
 * states its rules from the constants creativeLabels reads, and names no hidden page (rules-04 D re-review LOW 5).
 */
export const ruleNotesOf = (t: TFn): string[] => [
  t('creatives.notes.labels', {
    pct: Math.round(FATIGUE_CTR_DROP * 100),
    conversions: fmtCount(MIN_CONVERSIONS_30D), clicks: fmtCount(MIN_CLICKS_30D), impressions: fmtCount(MIN_IMPRESSIONS_30D),
    pool: MIN_POOL_SIZE,
  }),
  t('creatives.notes.pmax'),
  t('creatives.notes.viewRate'),
];

/**
 * Box 2's footnotes, in order (spec §3.3, §6): the Result window, the Result definition (failed, or Reach's caveat), the
 * creatives left out, then the rules.
 */
export const footnotesOf = (n: FootnoteInput, t: TFn): string[] => [
  n.resultEnd === null
    ? t('creatives.notes.noResultWindow')
    : t('creatives.notes.resultThrough', { date: fmtShortDate(n.resultEnd, t) }),
  ...(n.mappingFailed ? [t('creatives.notes.mappingFailed')] : []),
  ...(n.reach ? [t('looker.resultMapping.caveat')] : []),
  ...(n.unknownCurrency > 0 ? [t('creatives.notes.unknownCurrency', { n: n.unknownCurrency })] : []),
  ...ruleNotesOf(t),
];
